import { CssBaseline } from '@mui/material';
import { AnyAction, Store } from '@reduxjs/toolkit';
import BackgroundUploadProvider from 'components/background-upload/background-upload.context';
import ErrorBoundary from 'components/error-boundary/error-boundary.component';
import Head from 'components/head/head.component';
import IncSnackbarProvider from 'components/inc-snackbar-provider/inc-snackbar-provider.component';
import CenteredProgress from 'components/progress/centered-progress/centered-progress.component';
import Theme from 'components/theme/theme.component';
import { Suspense } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { getPersistor, makeStore } from 'redux/store';
import { TestDataRouter } from 'test-util/router/test-data-router.component';
import AfterRedux from './after-redux/after-redux.component';
import LoadMaps from './load-maps/load-maps.component';
import StatsigWrapper from './statsig/statsig-wrapper.component';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { IncentivioRouteObject } from 'pages/main-routes';

export type AppWrapperProps = {
  store?: Store<any, AnyAction>;
  children?: React.ReactNode;
};

export const RealAppWrapper = ({
  store = makeStore(),
  children,
}: AppWrapperProps) => {
  return (
    <Provider store={store}>
      <StatsigWrapper>
        <Theme>
          <PersistGate persistor={getPersistor()}>
            <AfterRedux />
            <ErrorBoundary name="app">
              <CssBaseline />
              <Head />
              <Suspense
                fallback={<CenteredProgress sx={{ position: 'absolute' }} />}
              >
                <IncSnackbarProvider>
                  <LoadMaps>
                    <BackgroundUploadProvider>
                      <LocalizationProvider dateAdapter={AdapterLuxon}>
                        {children}
                      </LocalizationProvider>
                    </BackgroundUploadProvider>
                  </LoadMaps>
                </IncSnackbarProvider>
              </Suspense>
            </ErrorBoundary>
          </PersistGate>
        </Theme>
      </StatsigWrapper>
    </Provider>
  );
};

const AppWrapper = ({
  store = makeStore(),
  children,
  routes,
}: AppWrapperProps & { routes?: IncentivioRouteObject[] }) => {
  return (
    <RealAppWrapper store={store}>
      <TestDataRouter routes={routes}>{children}</TestDataRouter>
    </RealAppWrapper>
  );
};

export default AppWrapper;
